import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, TextField, Button, Typography, InputAdornment, Select, MenuItem,
  Stack, Checkbox, Tooltip, DialogActions, DialogContent, DialogTitle,
  LinearProgress, Link, Autocomplete, Menu, useMediaQuery,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import ChecklistIcon from '@mui/icons-material/Checklist';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RuleIcon from '@mui/icons-material/Rule';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from './firebaseConfig';
import * as XLSX from 'xlsx';
import Filters from './Filters';
import NoMatchingRecords from './components/NoMatchingRecords';
import CustomerMergeDialog from './components/CustomerMergeDialog';
import AppDialog from './components/AppDialog';
import { useConfirm } from './components/ConfirmProvider';
import { useNotify } from './components/NotificationProvider';
import { useRole } from '../auth/RoleContext';
import { visiblePlants, codeLabel } from '../auth/permissions';
import { formatDate } from './utils/date';
import { useT } from '../i18n/LanguageContext';

// Brand palette (matches the Login redesign).
const C = {
  navy: '#0C447C', blue: '#185FA5', ghost: '#E6F1FB', pale: '#B5D4F4',
  amberBg: '#FAEEDA', amberBorder: '#FAC775', amberText: '#633806', amberBtnBorder: '#BA7517',
  greenBg: '#EAF3DE', greenText: '#27500A', border: '#D7E2EC',
  redBg: '#FDECEA', redBorder: '#F1B0AB', redText: '#B23A2E',
};

// Real ticket states (derived from reviewStatus / source), mapped to the spec's badge colors.
const STATUS = {
  PRINTED: { key: 'PRINTED', label: 'Printed', bg: C.greenBg, color: C.greenText },
  PENDING: { key: 'PENDING', label: 'Pending review', bg: C.amberBg, color: C.amberText },
  VERIFIED: { key: 'VERIFIED', label: 'Verified', bg: C.ghost, color: C.navy },
  ERROR: { key: 'ERROR', label: 'Error', bg: C.redBg, color: C.redText },
};
const STATUS_KEY = { PRINTED: 'status.printed', PENDING: 'status.pending', VERIFIED: 'status.verified', ERROR: 'status.error' };
const statusOf = (row) =>
  row.reviewStatus === 'ERROR' || row.scanError ? STATUS.ERROR
    : row.reviewStatus === 'PENDING' ? STATUS.PENDING
      : row.reviewStatus === 'VERIFIED' ? STATUS.VERIFIED
        : STATUS.PRINTED;

const parseDate = (s) => {
  if (!s) return null;
  const m = String(s).match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return new Date(+m[3], +m[1] - 1, +m[2]);
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

// Ticket "rate type" for the Type filter/badge. Tonnage weigh tickets (ticket_detail) are TON;
// field tickets (field_ticket) are HOUR or LOAD per their basis. STANDBY reserved for later.
const typeOf = (row) => (row.source === 'FIELD' ? (row.rateType || 'HOUR') : 'TON');

// Sentinel for the "(Unassigned)" Customer filter option (tickets with no customer).
const UNASSIGNED = '__UNASSIGNED__';

// Normalize a customer spelling the same way the backend does (FieldCustomerService.normalize:
// upper-case + strip non-alphanumerics) so a ticket's Billed-To can be matched to a merge alias.
const normKey = (s) => String(s ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');

// QTY column unit per rate type (§7): tons → "t", hourly → "hr", per-load → "load".
const unitOf = (row) => {
  const t = typeOf(row);
  return t === 'HOUR' ? 'hr' : t === 'LOAD' ? 'load' : 't';
};
const qtyDisplay = (row) => {
  const v = row.ton;
  if (v === null || v === undefined || v === '') return '—';
  return `${v} ${unitOf(row)}`;
};

// "Incomplete" = a ticket that can't yet flow to money: missing customer or truck, or a scan/truck
// error. (Rate-missing / DRAFT-master checks arrive with the resolution engine; this is the
// client-visible subset for now.)
const isIncomplete = (row) =>
  !(row.customerName || '').trim() || !(row.vehicle || '').toString().trim() || statusOf(row).key === 'ERROR';

// "Needs rate" = the ticket's FS/Job code doesn't resolve to a rate as of its date, so it would
// invoice/pay at $0 — silent lost money. Only flag when the backend explicitly said so (hasRate ===
// false); a missing flag (older payload) is left un-flagged rather than lighting up every row.
const needsRate = (row) => row.hasRate === false;

// Map a field_ticket API row into the unified ticket shape the table renders. Field tickets have no
// plant/tons/fs-code; quantity is hours or loads and basis stands in for the code column.
const mapField = (ft) => ({
  source: 'FIELD',
  fieldId: ft.id,
  ticketId: `FT-${ft.id}`,          // string id avoids colliding with numeric tonnage ticketIds
  rateType: String(ft.basis || '').toUpperCase(),   // HOUR | LOAD
  date: ft.ticketDate,              // ISO yyyy-MM-dd
  ticketNo: ft.ticketNo,
  companyName: null,
  customerName: ft.billedTo,
  ton: ft.quantity,                 // hours or loads (shown in the Qty column)
  quantity: ft.quantity,
  loads: ft.loads,                  // load count (kept for hourly, for the WB invoice)
  fsCode: String(ft.basis || '').toUpperCase(),
  vehicle: ft.truckNo,
  truckType: ft.truckType,
  producerLocation: ft.producerLocation,
  destination: ft.destination,
  driver: ft.driver,
  foreman: ft.foreman,
  reviewStatus: ft.reviewStatus,    // PENDING | VERIFIED
  hasRate: ft.rate != null && Number(ft.rate) > 0,  // priced by truck-type × basis; 0 = no rate
  hasImage: ft.hasImage,
  updatedBy: ft.updatedBy, updatedAt: ft.updatedAt,
  createdBy: ft.createdBy, createdAt: ft.createdAt,
});

const SORT_OPTIONS = [
  { value: 'DATE_DESC', label: 'Date (newest first)' },
  { value: 'DATE_ASC', label: 'Date (oldest first)' },
  { value: 'TICKET', label: 'Ticket number' },
  { value: 'CUSTOMER', label: 'Customer name' },
  { value: 'TONS_DESC', label: 'Qty (highest first)' },
];

// Add-ticket type selector: a weigh (tonnage) ticket vs a field ticket (HOUR/LOAD). Flatbed is added
// on its own screen. HOUR/LOAD write to field_ticket; TON writes to ticket_detail.
const TICKET_TYPE_OPTIONS = [
  { value: 'TON', label: 'Weigh ticket (Tons)' },
  { value: 'HOUR', label: 'Field ticket — Hourly' },
  { value: 'LOAD', label: 'Field ticket — Per load' },
];
const FIELD_TRUCK_TYPES = [
  { value: 'BELLY', label: 'Belly' },
  { value: 'END', label: 'End Dump' },
  { value: 'TANDEM', label: 'Tandem' },
  { value: 'TRI', label: 'Tri Axle' },
  { value: 'SUPER', label: 'Super Dumps' },
  { value: 'QUADS', label: 'Quads' },
  { value: 'FLAT', label: 'Flat Bed' },
  { value: 'FLOAT', label: 'Float' },
];

// Persist the last Tickets search (filters + view state) so navigating away (e.g. into the Verify
// workspace) and back restores what the user was looking at instead of resetting to a blank screen.
// We store the query, not the rows, and re-run the search on return so statuses are fresh (a ticket
// just verified in the workspace comes back as Verified). sessionStorage = per-tab, cleared on close.
const TICKETS_SEARCH_KEY = 'viai.ticketsSearch';
const readSavedSearch = () => {
  try { return JSON.parse(sessionStorage.getItem(TICKETS_SEARCH_KEY) || 'null') || {}; }
  catch { return {}; }
};

const Ticket = () => {
  const [tickets, setTickets] = useState([]);
  const [isTicketDtls, setIsTicketDtls] = useState(false);
  const [filterData, setFilterData] = useState(() => readSavedSearch().filterData || {});
  const [searched, setSearched] = useState(() => !!readSavedSearch().searched);
  const [loading, setLoading] = useState(false);

  // Inline edit (keyed by ticketId so it survives sorting/pagination).
  const [editId, setEditId] = useState(null);
  const [formValues, setFormValues] = useState({});

  // Redesign controls. Initial values restore from the last saved search (see TICKETS_SEARCH_KEY).
  const [search, setSearch] = useState(() => readSavedSearch().search || '');
  const [statusFilter, setStatusFilter] = useState(() => readSavedSearch().statusFilter || 'ALL');
  const [typeFilter, setTypeFilter] = useState(() => readSavedSearch().typeFilter || 'ALL');
  const [customerFilter, setCustomerFilter] = useState(() => readSavedSearch().customerFilter || 'ALL');
  const [sortBy, setSortBy] = useState(() => readSavedSearch().sortBy || 'DATE_DESC');
  const [page, setPage] = useState(() => readSavedSearch().page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(() => readSavedSearch().rowsPerPage || 10);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());

  // Dialogs.
  const [open, setOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);
  // Bumped after a rotate so the <img> refetches. The URL is otherwise identical and the browser
  // would keep showing the cached sideways copy, which reads as "the button did nothing".
  const [scanVersion, setScanVersion] = useState(0);
  const [rotating, setRotating] = useState(false);
  // What the server currently has for this scan, so a rotate is applied relative to the truth
  // rather than to a guess. Reset whenever a different ticket is opened.
  const [scanRotation, setScanRotation] = useState(0);
  const [fieldEditing, setFieldEditing] = useState(null); // field ticket being reviewed in the popup
  const [rateDialog, setRateDialog] = useState(null);     // inline "Add rate" popup: { fsCode, rate, effectiveDate, fromEdit } | null
  const [savingRate, setSavingRate] = useState(false);
  const [newTicket, setNewTicket] = useState({ type: 'TON', date: '', ticketNo: '', companyName: '', customerName: '', ton: '', fsCode: '', jobCode: '', vehicle: '', truckType: '', quantity: '', producerLocation: '', destination: '' });

  // Plants (vendors) for the Add-ticket "Plant" dropdown. On a weigh ticket the
  // "company" is the plant/vendor that issued it (CEMEX / Martin Marietta / Colorado);
  // the backend derives plant_id from this companyName, so we store the plant name.
  const [plants, setPlants] = useState([]);

  // Companies + inline "create truck" dialog (for fixing an unrecognized-truck error row
  // without leaving the Tickets screen). truckDialogRow holds the flagged ticket so we can
  // re-save it after the truck is created, which clears its stored scanError.
  const [companies, setCompanies] = useState([]);
  const [truckDialogRow, setTruckDialogRow] = useState(null);
  const [truckForm, setTruckForm] = useState({ number: '', companyId: '', truckType: '' });
  // Inline "add company" inside the Create-truck popup (so a new subcontractor can be added
  // without leaving the dialog / navigating to the Companies screen).
  const [addingCompany, setAddingCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [savingCompany, setSavingCompany] = useState(false);

  // Trucks (with their owning companyId) — used to apply the Subcontractor/Truck filters to the
  // field-ticket rows client-side, since /api/field-tickets has no server-side filter.
  const [trucks, setTrucks] = useState([]);

  const [user] = useAuthState(auth);
  const { role } = useRole();
  const isIvy = role === 'IVY_STAFF';
  const codeLbl = codeLabel(role); // 'Job Code' for IVY, else 'FS Code'
  const confirm = useConfirm();
  const notify = useNotify();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const t = useT();
  // Phones get cards instead of the table (no sideways scrolling) and a compact action bar.
  const isPhone = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const [actionsAnchor, setActionsAnchor] = useState(null);

  // "Review all" opens the Verify workspace scoped to EXACTLY what the grid is currently showing — the
  // Type / Status / Customer / search filters are honored, not just the date range — so you walk the
  // filtered subset, not every outstanding ticket at once. We pass the visible rows' identity keys
  // (matching VerifyWorkspace's row.key: T-<ticketId> / F-<fieldId>) and the queue restricts to them.
  const goReviewAll = () => {
    const q = new URLSearchParams({ scope: 'all' });
    ['startDate', 'endDate', 'company', 'truck', 'companyName', 'plant'].forEach((k) => {
      if (filterData?.[k]) q.set(k, filterData[k]);
    });
    const onlyKeys = sorted.map((r) => (r.source === 'FIELD' ? `F-${r.fieldId}` : `T-${r.ticketId}`));
    navigate(`/Verify?${q.toString()}`, { state: { onlyKeys } });
  };

  // Open the flagged queue scoped to the SAME filters as the grid, so "Verify flagged (N)" opens
  // exactly those N tickets (previously it navigated to /Verify with no params, which loaded the
  // all-time review queue — the count and the queue disagreed).
  const goVerifyFlagged = () => {
    const q = new URLSearchParams();
    ['startDate', 'endDate', 'company', 'truck', 'companyName', 'plant'].forEach((k) => {
      if (filterData?.[k]) q.set(k, filterData[k]);
    });
    const qs = q.toString();
    navigate(qs ? `/Verify?${qs}` : '/Verify');
  };

  // Open the inline "Add rate" popup for a ticket's code — no navigation. Defaults the effective date to
  // the ticket's date so the new rate actually applies to it.
  const isoOf = (d) => {
    const dt = parseDate(d);
    if (!dt) return new Date().toISOString().split('T')[0];
    const p = (n) => String(n).padStart(2, '0');
    return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
  };
  // Open the rate popup for a code. Default the effective date to the search Start Date (not the single
  // clicked ticket's date) so one rate covers every ticket of this code in the period. Also look up any
  // EXISTING rate for the code so the popup can offer Update (change it) as well as Add-new-date.
  const openAddRate = async (row) => {
    const fsCode = row?.fsCode || '';
    setRateDialog({
      fsCode,
      rate: '',
      effectiveDate: filterData?.startDate || isoOf(row?.date),
      fromEdit: editId != null,
      existing: null,
    });
    if (!fsCode) return;
    try {
      // Latest existing rate for this code (as of a far-future date), if any.
      const res = await axios.get(`${apiUrl}/api/master/rate-cards`, { params: { asOf: '2099-12-31', cardType: 'SUB' } });
      const rows = Array.isArray(res.data) ? res.data : [];
      const hit = rows.find((r) => String(r.fsCode) === String(fsCode));
      setRateDialog((p) => (p && p.fsCode === fsCode
        ? { ...p, existing: hit ? { id: hit.id, rate: hit.rate, effectiveDate: hit.effectiveDate } : null,
            rate: hit ? String(hit.rate ?? '') : '' }
        : p));
    } catch { /* treat as add-only */ }
  };

  // Save the rate — create a new effective-dated row (POST) or update the existing one (PUT).
  const submitRate = async (mode) => {
    const fsCode = (rateDialog.fsCode || '').trim();
    if (!fsCode) { notify.warning('Code is required.'); return; }
    if (rateDialog.rate === '' || rateDialog.rate == null || isNaN(Number(rateDialog.rate))) {
      notify.warning('Enter a rate.'); return;
    }
    if (!rateDialog.effectiveDate) { notify.warning('Effective date is required.'); return; }
    const body = {
      fsCode, rate: Number(rateDialog.rate), effectiveDate: rateDialog.effectiveDate,
      cardType: 'SUB', truckType: 'ALL',
    };
    try {
      setSavingRate(true);
      if (mode === 'update' && rateDialog.existing?.id) {
        await axios.put(`${apiUrl}/api/master/rate-cards/${rateDialog.existing.id}`, body);
        notify.success(`Rate updated for ${fsCode}`);
      } else {
        await axios.post(`${apiUrl}/api/master/rate-cards`, body);
        notify.success(`Rate added for ${fsCode}`);
      }
      if (rateDialog.fromEdit) setFormValues((p) => ({ ...p, hasRate: true }));
      setRateDialog(null);
      await refetch();   // recompute hasRate so the "No rate" flag clears
    } catch (e) {
      notify.error(e?.response?.data?.message || e?.response?.data || 'Failed to save rate');
    } finally {
      setSavingRate(false);
    }
  };

  useEffect(() => {
    axios.get(`${apiUrl}/api/master/plants`)
      .then((res) => setPlants(res.data || []))
      .catch((err) => console.debug('plants unavailable', err?.message));
    axios.get(`${apiUrl}/api/master/companies`)
      .then((res) => setCompanies(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.debug('companies unavailable', err?.message));
  }, [apiUrl]);

  // Trucks lookup (truckNumber + owning companyId), same source the Filters dropdown uses.
  useEffect(() => {
    if (!user?.email) return;
    axios.get(`${apiUrl}/api/invoice/lookups`, { params: { email: user.email } })
      .then((res) => setTrucks(res.data?.truckNumberLookupList || []))
      .catch((err) => console.debug('truck lookups unavailable', err?.message));
  }, [apiUrl, user]);

  const refetch = async (params = filterData) => {
    // Unified list = tonnage weigh tickets (ticket_detail) + field tickets (field_ticket, HOUR/LOAD).
    // Flatbed is intentionally NOT here (separate broker/factored flow). Field tickets have no
    // server-side filter, so fetch all and narrow by the selected date range client-side.
    const [tonRes, fieldRes, aliasRes] = await Promise.allSettled([
      axios.get(`${apiUrl}/api/invoice/ticket-details`, { params }),
      axios.get(`${apiUrl}/api/field-tickets`),
      axios.get(`${apiUrl}/api/field-tickets/customer-aliases`),
    ]);
    const tonRows = tonRes.status === 'fulfilled' ? (tonRes.value.data || []) : [];
    // Resolve field-ticket customer spellings to their merged canonical name (read-time, mirrors the
    // invoice) so a customer merge visibly collapses the grid + the Customer filter too. The original
    // stays on rawCustomer. Tonnage tickets are left as-is (aliases are a field-ticket customer tool).
    const aliasList = aliasRes.status === 'fulfilled' && Array.isArray(aliasRes.value.data) ? aliasRes.value.data : [];
    const aliasMap = new Map(aliasList.map((a) => [a.sourceKey, a.canonicalName]));
    const resolveCustomer = (name) => aliasMap.get(normKey(name)) || name;
    let fieldRows = fieldRes.status === 'fulfilled' ? (fieldRes.value.data || []).map(mapField) : [];
    fieldRows = fieldRows.map((r) => ({ ...r, rawCustomer: r.customerName, customerName: resolveCustomer(r.customerName) }));
    const { startDate, endDate, truck, company, plant } = params || {};
    if (startDate) fieldRows = fieldRows.filter((r) => !r.date || r.date >= startDate);
    if (endDate) fieldRows = fieldRows.filter((r) => !r.date || r.date <= endDate);
    // Mirror the ticket-details server-side filters on the field-ticket rows (they come back
    // unfiltered). A Plant selection excludes field tickets entirely — they have no plant.
    // Subcontractor is matched by the truck's owning company, same basis as the Truck dropdown.
    if (plant) {
      fieldRows = [];
    } else {
      if (truck) fieldRows = fieldRows.filter((r) => String(r.vehicle) === String(truck));
      if (company) {
        const companyTruckNos = new Set(
          trucks
            .filter((t) => String(t.companyId) === String(company))
            .map((t) => String(t.truckNumber))
        );
        fieldRows = fieldRows.filter((r) => companyTruckNos.has(String(r.vehicle)));
      }
    }
    const rows = [...tonRows, ...fieldRows];
    setTickets(rows);
    setIsTicketDtls(rows.length > 0);
    return rows;
  };

  // On mount, if a previous search was saved (e.g. we just came back from Verify), re-run it so the
  // table repopulates automatically with fresh results for the same filters.
  useEffect(() => {
    const saved = readSavedSearch();
    if (saved?.searched && saved.filterData) {
      setLoading(true);
      refetch(saved.filterData).finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist the current search (query + view state, not the rows) whenever it changes.
  useEffect(() => {
    if (!searched) return;
    try {
      sessionStorage.setItem(TICKETS_SEARCH_KEY, JSON.stringify({
        filterData, searched, search, statusFilter, typeFilter, customerFilter, sortBy, page, rowsPerPage,
      }));
    } catch { /* storage full / disabled — non-fatal */ }
  }, [filterData, searched, search, statusFilter, typeFilter, customerFilter, sortBy, page, rowsPerPage]);

  const handleFilterChange = async (fd) => {
    try {
      setLoading(true);
      setTickets([]);
      setIsTicketDtls(false);
      setSearched(true);
      setSelected(new Set());
      setPage(0);
      setCustomerFilter('ALL');
      setFilterData(fd);
      await refetch(fd);
    } catch (error) {
      console.error('Error fetching filtered invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  // ---- field-ticket review popup (handwritten HOUR/LOAD, reviewed against the scan) ----
  const openFieldReview = (row) => setFieldEditing({
    id: row.fieldId,
    ticketNo: row.ticketNo,
    truckNo: row.vehicle,
    ticketDate: row.date,
    // Edit the ORIGINAL stored spelling, not the merged canonical display, so saving doesn't silently
    // overwrite the ticket's real Billed-To with the alias target.
    billedTo: row.rawCustomer ?? row.customerName,
    truckType: row.truckType,
    basis: row.rateType,
    quantity: row.quantity ?? row.ton,
    loads: row.loads,
    producerLocation: row.producerLocation,
    destination: row.destination,
    driver: row.driver,
    foreman: row.foreman,
    hasImage: row.hasImage,
    reviewStatus: row.reviewStatus,
  });
  const setF = (k, v) => setFieldEditing((p) => ({ ...p, [k]: v }));

  const saveFieldEdit = async () => {
    try {
      await axios.put(`${apiUrl}/api/field-tickets/${fieldEditing.id}`, { ...fieldEditing, email: user?.email });
      notify.success('Field ticket updated');
      return true;
    } catch (error) {
      notify.error(error?.response?.data || 'Save failed');
      return false;
    }
  };
  const verifyField = async () => {
    const r = fieldEditing;
    if (!r.billedTo || !r.truckType || !r.basis || !r.quantity) {
      notify.warning('Set Billed To, truck type, basis and quantity before verifying.');
      return false;
    }
    try {
      await axios.post(`${apiUrl}/api/field-tickets/${r.id}/verify`, null, { params: { email: user?.email } });
      notify.success(`Ticket ${r.ticketNo || r.id} verified`);
      return true;
    } catch (error) {
      notify.error(error?.response?.data || 'Verify failed');
      return false;
    }
  };

  // ---- inline edit ----
  const handleEdit = (row) => {
    if (row.source === 'FIELD') { openFieldReview(row); return; }
    setEditId(row.ticketId);
    setFormValues({ ...row });
    // Seed from what the server holds for THIS scan, so the first quarter turn is applied to the
    // current angle rather than to an assumed 0 — which would undo an existing correction.
    setScanRotation(row.documentRotation ?? 0);
    setScanVersion((v) => v + 1);
  };
  /**
   * Turn the stored scan a quarter turn and persist it. The server owns the current value, so the
   * delta is resolved against what it last told us rather than tracked separately here — two quick
   * clicks then cannot race into a wrong absolute angle.
   */
  const rotateScan = async (delta) => {
    if (!formValues.documentId || rotating) return;
    setRotating(true);
    try {
      const next = (((scanRotation + delta) % 360) + 360) % 360;
      const res = await axios.patch(
        `${apiUrl}/api/documents/${formValues.documentId}/rotation`, null, { params: { degrees: next } });
      setScanRotation(res.data?.rotationDegrees ?? next);
      setScanVersion((v) => v + 1);
    } catch (err) {
      console.error('Could not rotate the scan', err);
    } finally {
      setRotating(false);
    }
  };

  const handleCancel = () => { setEditId(null); setFormValues({}); setScanRotation(0); };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((p) => ({ ...p, [name]: value }));
  };
  const handleSave = async () => {
    try {
      await axios.put(`${apiUrl}/api/invoice/update-ticket-details`, { ...formValues });
      await refetch();
      setEditId(null);
      notify.success('Ticket updated');
    } catch (error) {
      console.error('Error updating the row:', error);
      notify.error('Failed to update ticket');
    }
  };

  const handleVerify = async (row) => {
    if (row.source === 'FIELD') {
      // Handwritten — open the review popup so it's checked against the scan before verifying.
      openFieldReview(row);
      return;
    }
    try {
      await axios.put(`${apiUrl}/api/invoice/verify-ticket/${row.ticketId}?email=${encodeURIComponent(user?.email || '')}`);
      await refetch();
      notify.success('Ticket marked reviewed');
    } catch (error) {
      console.error('Error verifying the ticket:', error);
      // Surface the server reason (e.g. an unrecognized truck blocks verification) instead of a generic message.
      notify.error(error?.response?.data?.message || error?.response?.data || 'Failed to mark reviewed');
    }
  };

  // ---- inline create-truck (fixes an unrecognized-truck error row in place) ----
  // Loose match so OCR spelling doesn't defeat it: "C & C TRANSPORT" must find "C&C Transport", and
  // "KJ CARRIERS LLC" must find "KJ CARRIERS".
  const squashName = (s) => String(s ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const matchCompanyId = (name) => {
    const n = squashName(name);
    if (!n) return '';
    const exact = companies.find((c) => squashName(c.name) === n);
    if (exact) return exact.id;
    const partial = companies.find((c) => {
      const cn = squashName(c.name);
      return cn.length > 3 && n.length > 3 && (cn.includes(n) || n.includes(cn));
    });
    return partial ? partial.id : '';
  };

  const openCreateTruck = (row) => {
    // Every vendor prints the truck's owner in the Vehicle field ("1034 KJ CARRIERS LLC"), and we now
    // capture it — so preselect the company instead of making the operator retype it per sub. A truck
    // number is only unique within a company, so this is also the field that makes it meaningful.
    const suggested = matchCompanyId(row?.truckCompany);
    setTruckForm({ number: row.vehicle || '', companyId: suggested, truckType: '' });
    // Named a company we don't have yet? Offer it as the new-company name rather than a blank box.
    setAddingCompany(false);
    setNewCompanyName(row?.truckCompany && !suggested ? row.truckCompany : '');
    setTruckDialogRow(row);
  };
  const closeCreateTruck = () => {
    setTruckDialogRow(null);
    setAddingCompany(false);
    setNewCompanyName('');
  };
  // Create a company inline (from the Create-truck popup) and select it, no navigation.
  const handleAddCompanyInline = async () => {
    const name = newCompanyName.trim();
    if (!name) { notify.warning('Enter a company name.'); return; }
    try {
      setSavingCompany(true);
      const res = await axios.post(`${apiUrl}/api/master/companies`, { name });
      const created = res.data;
      setCompanies((prev) =>
        [...prev, created].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      setTruckForm((f) => ({ ...f, companyId: created.id }));
      setNewCompanyName('');
      setAddingCompany(false);
      notify.success(`Company "${created.name}" added.`);
    } catch (error) {
      notify.error(error?.response?.data?.message || error?.response?.data || 'Failed to add company');
    } finally {
      setSavingCompany(false);
    }
  };
  const handleCreateTruck = async () => {
    const number = String(truckForm.number || '').trim();
    if (!number || !truckForm.companyId) {
      notify.error('Truck number and company are required');
      return;
    }
    try {
      await axios.post(`${apiUrl}/api/master/trucks`, {
        number,
        companyId: truckForm.companyId,
        ...(truckForm.truckType ? { truckType: truckForm.truckType } : {}),
      });
      // Re-save the flagged ticket with this truck number so the backend re-resolves it and
      // clears the stored scanError (creating the truck alone doesn't touch the ticket row).
      if (truckDialogRow) {
        await axios.put(`${apiUrl}/api/invoice/update-ticket-details`, {
          ...truckDialogRow, vehicle: number, email: user?.email,
        });
      }
      await refetch();
      closeCreateTruck();
      notify.success(`Truck ${number} created — ticket updated`);
    } catch (error) {
      console.error('Error creating truck:', error);
      const msg = error?.response?.data?.message || error?.response?.data || 'Failed to create truck';
      // 409 "already exists" is not a failure — it proves the truck IS there, so the ticket's
      // "not recognized" is a stale flag from scan time. Without this the create aborts before the
      // ticket is re-saved, and the row stays flagged with no way to clear it from this screen.
      if (String(msg).toLowerCase().includes('already exists')) {
        try {
          if (truckDialogRow) {
            await axios.put(`${apiUrl}/api/invoice/update-ticket-details`, {
              ...truckDialogRow, vehicle: number, email: user?.email,
            });
          }
          await refetch();   // the list re-checks every flagged truck server-side
          closeCreateTruck();
          notify.success(`Truck ${number} already exists — the ticket's flag was out of date, now cleared.`);
          return;
        } catch (e) {
          notify.error(e?.response?.data?.message || e?.response?.data
            || 'Could not clear the stale flag');
          return;
        }
      }
      notify.error(msg);
    }
  };

  // One-click fix for a likely OCR misread: set the ticket's FS code to the suggested known code.
  const applySuggestedCode = async (row) => {
    try {
      await axios.put(`${apiUrl}/api/invoice/update-ticket-details`, {
        ...row, fsCode: row.suggestedFsCode, email: user?.email,
      });
      notify.success(`FS code corrected to ${row.suggestedFsCode}`);
      await refetch();
    } catch (error) {
      notify.error(error?.response?.data?.message || error?.response?.data || 'Failed to update code');
    }
  };

  const handleDelete = async (row) => {
    const ok = await confirm({
      title: 'Delete ticket',
      message: 'Delete this ticket? It will no longer be included in invoices, payroll, or reports. This cannot be undone.',
      confirmText: 'Delete', severity: 'error',
    });
    if (!ok) return;
    try {
      if (row.source === 'FIELD') {
        await axios.delete(`${apiUrl}/api/field-tickets/${row.fieldId}`);
      } else {
        await axios.put(`${apiUrl}/api/invoice/delete-ticket-detail/${row.ticketId}`);
      }
      await refetch();
      notify.success('Ticket deleted');
    } catch (error) {
      console.error('Error deleting the row:', error);
      notify.error('Failed to delete ticket');
    }
  };

  // ---- add (weigh ticket → ticket_detail, or field ticket HOUR/LOAD → field_ticket) ----
  const handleAddTicket = async () => {
    try {
      if (newTicket.type === 'TON') {
        const body = {
          date: newTicket.date, ticketNo: newTicket.ticketNo, companyName: newTicket.companyName,
          customerName: newTicket.customerName, ton: newTicket.ton, fsCode: newTicket.fsCode,
          jobCode: newTicket.jobCode, vehicle: newTicket.vehicle, email: user?.email,
        };
        await axios.post(`${apiUrl}/api/invoice/add-ticket-details`, body);
      } else {
        // HOUR / LOAD → field ticket. Truck No maps to truckNo, Customer to billedTo, basis = type.
        const body = {
          ticketNo: newTicket.ticketNo,
          truckNo: newTicket.vehicle,
          ticketDate: newTicket.date || null,
          billedTo: newTicket.customerName,
          truckType: newTicket.truckType,
          basis: newTicket.type,
          quantity: newTicket.quantity !== '' ? newTicket.quantity : null,
          producerLocation: newTicket.producerLocation,
          destination: newTicket.destination,
        };
        await axios.post(`${apiUrl}/api/field-tickets?email=${encodeURIComponent(user?.email || '')}`, body);
      }
      setNewTicket({ type: 'TON', date: '', ticketNo: '', companyName: '', customerName: '', ton: '', fsCode: '', jobCode: '', vehicle: '', truckType: '', quantity: '', producerLocation: '', destination: '' });
      setOpen(false);
      if (searched) await refetch();
      notify.success('Ticket added');
    } catch (error) {
      console.error('Error adding ticket:', error);
      notify.error(error?.response?.data || 'Failed to add ticket');
    }
  };
  const addHandleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTicket((p) => ({ ...p, [name]: value }));
  };

  // Tickets still needing a human (drives the "Verify flagged" deep-link into the workspace).
  const flaggedCount = tickets.filter(
    (r) => r.reviewStatus === 'PENDING' || statusOf(r).key === 'ERROR').length;

  // ---- derived list ----
  const term = search.trim().toLowerCase();
  const searchFiltered = term
    ? tickets.filter((r) =>
      [r.ticketNo, r.customerName, r.companyName].some((v) => String(v || '').toLowerCase().includes(term)))
    : tickets;

  const typeCounts = {
    ALL: searchFiltered.length,
    TON: searchFiltered.filter((r) => typeOf(r) === 'TON').length,
    HOUR: searchFiltered.filter((r) => typeOf(r) === 'HOUR').length,
    LOAD: searchFiltered.filter((r) => typeOf(r) === 'LOAD').length,
  };
  const typeFiltered = typeFilter === 'ALL'
    ? searchFiltered
    : searchFiltered.filter((r) => typeOf(r) === typeFilter);

  // Customer filter (client-side): options + counts come only from the loaded range, so there are no
  // dead selections. "(Unassigned)" surfaces tickets with no customer so missing data stays findable (§7).
  const customerAgg = new Map();
  let unassignedCustomerCount = 0;
  typeFiltered.forEach((r) => {
    const name = (r.customerName || '').trim();
    if (!name) { unassignedCustomerCount += 1; return; }
    customerAgg.set(name, (customerAgg.get(name) || 0) + 1);
  });
  const customerOptions = [...customerAgg.entries()].sort((a, b) => a[0].localeCompare(b[0]));

  const customerFiltered =
    customerFilter === 'ALL' ? typeFiltered
      : customerFilter === UNASSIGNED ? typeFiltered.filter((r) => !(r.customerName || '').trim())
        : typeFiltered.filter((r) => (r.customerName || '').trim() === customerFilter);

  // Loud remainder (§7): unassigned tickets hidden because a specific customer is selected.
  const hiddenUnassignedCount =
    (customerFilter !== 'ALL' && customerFilter !== UNASSIGNED) ? unassignedCustomerCount : 0;

  const counts = {
    ALL: customerFiltered.length,
    ERROR: customerFiltered.filter((r) => statusOf(r).key === 'ERROR').length,
    PRINTED: customerFiltered.filter((r) => statusOf(r).key === 'PRINTED').length,
    PENDING: customerFiltered.filter((r) => statusOf(r).key === 'PENDING').length,
    VERIFIED: customerFiltered.filter((r) => statusOf(r).key === 'VERIFIED').length,
    INCOMPLETE: customerFiltered.filter(isIncomplete).length,
    NEEDS_RATE: customerFiltered.filter(needsRate).length,
  };

  const statusFiltered =
    statusFilter === 'ALL' ? customerFiltered
      : statusFilter === 'INCOMPLETE' ? customerFiltered.filter(isIncomplete)
        : statusFilter === 'NEEDS_RATE' ? customerFiltered.filter(needsRate)
          : customerFiltered.filter((r) => statusOf(r).key === statusFilter);

  const sorted = [...statusFiltered].sort((x, y) => {
    switch (sortBy) {
      case 'DATE_ASC': return (parseDate(x.date)?.getTime() || 0) - (parseDate(y.date)?.getTime() || 0);
      case 'TICKET': return String(x.ticketNo || '').localeCompare(String(y.ticketNo || ''), undefined, { numeric: true });
      case 'CUSTOMER': return String(x.customerName || '').localeCompare(String(y.customerName || ''));
      case 'TONS_DESC': return (parseFloat(y.ton) || 0) - (parseFloat(x.ton) || 0);
      case 'DATE_DESC':
      default: return (parseDate(y.date)?.getTime() || 0) - (parseDate(x.date)?.getTime() || 0);
    }
  });

  const pageCount = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = sorted.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);
  const firstRow = sorted.length === 0 ? 0 : safePage * rowsPerPage + 1;
  const lastRow = Math.min(sorted.length, safePage * rowsPerPage + rowsPerPage);

  // ---- selection ----
  const toggleRow = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const allSelected = sorted.length > 0 && sorted.every((r) => selected.has(r.ticketId));
  const someSelected = sorted.some((r) => selected.has(r.ticketId)) && !allSelected;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(sorted.map((r) => r.ticketId)));
  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()); };

  // ---- bulk actions ----
  const bulkDelete = async () => {
    const ids = [...selected];
    const ok = await confirm({
      title: 'Delete tickets',
      message: `Delete ${ids.length} selected ticket(s)? They will no longer be included in invoices, payroll, or reports. This cannot be undone.`,
      confirmText: 'Delete', severity: 'error',
    });
    if (!ok) return;
    const byId = new Map(sorted.map((r) => [r.ticketId, r]));
    const results = await Promise.allSettled(ids.map((id) => {
      const r = byId.get(id);
      return r?.source === 'FIELD'
        ? axios.delete(`${apiUrl}/api/field-tickets/${r.fieldId}`)
        : axios.put(`${apiUrl}/api/invoice/delete-ticket-detail/${id}`);
    }));
    const failed = results.filter((r) => r.status === 'rejected').length;
    await refetch();
    setSelected(new Set());
    if (failed) notify.warning(`Deleted ${ids.length - failed}; ${failed} could not be deleted.`);
    else notify.success(`Deleted ${ids.length} ticket(s)`);
  };

  const bulkMarkReviewed = async () => {
    // Handwritten field tickets are excluded — they must be reviewed against their scan on Field Tickets.
    const targets = sorted.filter((r) => selected.has(r.ticketId) && r.reviewStatus === 'PENDING' && r.source !== 'FIELD');
    if (targets.length === 0) { notify.info('None of the selected tickets are pending review.'); return; }
    const results = await Promise.allSettled(targets.map((r) =>
      axios.put(`${apiUrl}/api/invoice/verify-ticket/${r.ticketId}?email=${encodeURIComponent(user?.email || '')}`)));
    const failed = results.filter((r) => r.status === 'rejected').length;
    await refetch();
    setSelected(new Set());
    if (failed) notify.warning(`Marked ${targets.length - failed} reviewed; ${failed} failed.`);
    else notify.success(`Marked ${targets.length} ticket(s) reviewed`);
  };

  const bulkInvoice = () => {
    // No batch-invoice endpoint yet; invoicing is grouped on the Invoice screens.
    notify.info('Batch invoicing isn’t available yet — use the Invoice screen to generate invoices for these tickets.');
  };

  // ---- bulk edit: set Customer / load type on many selected tickets at once ----
  // Fixes the OCR spelling-drift cleanup by hand (many "W Broth..." variants → one customer) without
  // opening each ticket. Customer applies to both tonnage + field tickets; load Type/Basis apply to
  // field tickets only (tonnage weigh tickets have no truck-type/basis).
  const [mergeOpen, setMergeOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkCustomer, setBulkCustomer] = useState('');
  const [bulkTruckType, setBulkTruckType] = useState('');
  const [bulkBasis, setBulkBasis] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const openBulkEdit = () => {
    setBulkCustomer(''); setBulkTruckType(''); setBulkBasis('');
    setBulkEditOpen(true);
  };

  const applyBulkEdit = async () => {
    const rowsSel = sorted.filter((r) => selected.has(r.ticketId));
    const customer = bulkCustomer.trim();
    const truckType = bulkTruckType;
    const basis = bulkBasis;
    if (!customer && !truckType && !basis) {
      notify.warning('Set a Customer, Truck type or Basis to apply.'); return;
    }
    const fieldRows = rowsSel.filter((r) => r.source === 'FIELD');
    const tonRows = rowsSel.filter((r) => r.source !== 'FIELD');
    // Tonnage rows can only take a Customer change; skip them when only type/basis was chosen.
    const tonTargets = customer ? tonRows : [];
    const skippedTon = customer ? 0 : tonRows.length;
    const fieldsChosen = [customer && 'Customer', truckType && 'Truck type', basis && 'Basis'].filter(Boolean).join(', ');

    const ok = await confirm({
      title: 'Update tickets',
      message: `Apply ${fieldsChosen} to ${fieldRows.length + tonTargets.length} ticket(s)?`
        + (skippedTon ? ` (${skippedTon} weigh ticket(s) will be skipped — Truck type/Basis apply to field tickets only.)` : ''),
      confirmText: 'Update',
    });
    if (!ok) return;
    setBulkBusy(true);

    // Field tickets: fetch the full DTO, patch, PUT — so untouched fields are preserved (the grid row
    // is a reduced shape and would blank fields if PUT directly).
    const fieldTasks = fieldRows.map(async (r) => {
      const { data: ft } = await axios.get(`${apiUrl}/api/field-tickets/${r.fieldId}`);
      return axios.put(`${apiUrl}/api/field-tickets/${r.fieldId}`, {
        ...ft,
        ...(customer ? { billedTo: customer } : {}),
        ...(truckType ? { truckType } : {}),
        ...(basis ? { basis } : {}),
        email: user?.email,
      });
    });
    // Tonnage tickets: the grid row IS the full InvoiceOcrDetails DTO, so patch customerName + round-trip.
    const tonTasks = tonTargets.map((r) =>
      axios.put(`${apiUrl}/api/invoice/update-ticket-details`, { ...r, customerName: customer }));

    const results = await Promise.allSettled([...fieldTasks, ...tonTasks]);
    setBulkBusy(false);
    const total = fieldTasks.length + tonTasks.length;
    const failed = results.filter((x) => x.status === 'rejected').length;
    setBulkEditOpen(false);
    setSelected(new Set());
    await refetch();
    if (failed === 0) {
      notify.success(`Updated ${total} ticket(s)${skippedTon ? ` · skipped ${skippedTon} weigh ticket(s)` : ''}.`);
    } else {
      notify.warning(`Updated ${total - failed} of ${total}; ${failed} failed${skippedTon ? ` · skipped ${skippedTon} weigh ticket(s)` : ''}.`);
    }
  };

  // Customer suggestions for the bulk field = the customer names on the selected rows (so the target
  // canonical spelling is one click away).
  const bulkCustomerOptions = Array.from(new Set(
    sorted.filter((r) => selected.has(r.ticketId)).map((r) => r.customerName).filter(Boolean)));

  // ---- export (from data, so it covers all pages/filters, not just the visible page) ----
  const generateExcel = () => {
    const rows = sorted.map((r) => ({
      Date: formatDate(r.date), 'Ticket No': r.ticketNo, Type: typeOf(r), Company: r.companyName, Customer: r.customerName,
      'Qty/Tons': r.ton, [codeLbl]: r.fsCode, Truck: r.vehicle,
      'Last Updated By': r.updatedBy || r.createdBy || '', 'Last Updated': r.updatedAt || r.createdAt || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tickets');
    const now = new Date();
    const company = (typeof filterData.companyName === 'string' && filterData.companyName) ? filterData.companyName.replace(/\s+/g, '-') : 'tickets';
    XLSX.writeFile(wb, `${company}_${now.toLocaleDateString()}_${now.toLocaleTimeString()}.xlsx`.replace(/[:/]/g, '-'));
  };

  // ---- title subline ----
  const fmtDay = (d) => { const dt = new Date(`${d}T00:00:00`); return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); };
  const company = (typeof filterData.companyName === 'string') ? filterData.companyName : '';
  const range = (filterData.startDate && filterData.endDate) ? `${fmtDay(filterData.startDate)} – ${fmtDay(filterData.endDate)}` : '';
  const subline = [company, range].filter(Boolean).join(' · ');

  // Where a ticket's stored scan lives (weigh tickets: ticket_document; field tickets: their own image).
  const scanOf = (row) => ({
    hasScan: row.source === 'FIELD' ? row.hasImage : !!row.documentId,
    scanUrl: row.source === 'FIELD'
      ? `${apiUrl}/api/field-tickets/${row.fieldId}/image`
      : `${apiUrl}/api/documents/${row.documentId}`,
  });

  // Phone totals strip: quantity summed per unit over everything the filters match (all pages).
  // Display only — invoice/pay money math stays in the backend.
  const phoneTotals = sorted.reduce((acc, r) => {
    const n = Number(r.ton);
    if (Number.isFinite(n)) acc[typeOf(r)] = Math.round((acc[typeOf(r)] + n) * 100) / 100;
    return acc;
  }, { TON: 0, HOUR: 0, LOAD: 0 });

  // One ticket as a phone card: what it is on top, the numbers in the middle, big buttons below.
  const renderCard = (row) => {
    const st = statusOf(row);
    const isSel = selected.has(row.ticketId);
    const { hasScan, scanUrl } = scanOf(row);
    return (
      <Box
        key={row.ticketId}
        onClick={selectMode ? () => toggleRow(row.ticketId) : undefined}
        sx={{
          px: 2, py: 1.5, borderBottom: `0.5px solid ${C.border}`,
          ...(st.key === 'ERROR' && { bgcolor: C.redBg, boxShadow: `inset 3px 0 0 ${C.redText}` }),
          ...(selectMode && isSel && { bgcolor: C.ghost }),
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          {selectMode && (
            <Checkbox checked={isSel} onChange={() => toggleRow(row.ticketId)} onClick={(e) => e.stopPropagation()} sx={{ mt: -0.75, ml: -1 }} inputProps={{ 'aria-label': `Select ticket ${row.ticketNo || ''}` }} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
              <Typography sx={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 600, color: C.blue }}>
                {row.ticketNo || '—'}
                {row.source === 'FIELD' && (
                  <Box component="span" sx={{ ml: 0.75, px: 0.5, borderRadius: 1, bgcolor: C.amberBg, color: C.amberText, fontFamily: 'system-ui', fontSize: 11, fontWeight: 600 }}>
                    {row.rateType}
                  </Box>
                )}
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#6A7682', whiteSpace: 'nowrap' }}>{formatDate(row.date)}</Typography>
            </Stack>
            <Typography noWrap sx={{ fontSize: 15, color: '#17202A', mt: 0.25 }}>{row.customerName || '—'}</Typography>
            {row.companyName && (
              <Typography noWrap sx={{ fontSize: 12, color: '#6A7682' }}>{row.companyName}</Typography>
            )}
            <Stack direction="row" spacing={2} alignItems="flex-end" sx={{ mt: 1 }}>
              <Box>
                <Typography sx={{ fontSize: 11, color: '#8A97A4' }}>{t('col.qty')}</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{qtyDisplay(row)}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, color: '#8A97A4' }}>{isIvy ? 'Job' : 'FS'}</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{row.fsCode || '—'}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, color: '#8A97A4' }}>{t('col.truck')}</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: row.scanError ? C.redText : 'inherit' }}>
                  {row.vehicle || (row.scanError ? t('tickets.noTruck') : '—')}
                </Typography>
              </Box>
              <Box sx={{ ml: 'auto !important', px: 1, py: 0.25, borderRadius: 10, bgcolor: st.bg, color: st.color, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                {t(STATUS_KEY[st.key])}
              </Box>
            </Stack>
            {row.scanError && (
              <Box sx={{ mt: 1, fontSize: 13, color: C.redText }}>
                <WarningAmberIcon sx={{ fontSize: 15, verticalAlign: 'text-bottom', mr: 0.5 }} />
                {row.scanError}{' '}
                <Link component="button" type="button" onClick={(e) => { e.stopPropagation(); openCreateTruck(row); }} sx={{ fontSize: 13, fontWeight: 600, color: C.redText }}>
                  {t('tickets.createTruck')}
                </Link>
              </Box>
            )}
            {needsRate(row) && (
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); openAddRate(row); }}
                  sx={{ textTransform: 'none', color: C.amberText, borderColor: C.amberBorder, bgcolor: C.amberBg }}>
                  {t('tickets.noRateAdd')}
                </Button>
                {row.suggestedFsCode && (
                  <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); applySuggestedCode(row); }}
                    sx={{ textTransform: 'none', color: C.navy, borderColor: C.pale, bgcolor: C.ghost }}>
                    {t('tickets.didYouMean', { code: row.suggestedFsCode })}
                  </Button>
                )}
              </Stack>
            )}
          </Box>
        </Stack>
        {!selectMode && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
            {st.key === 'PENDING' && (
              <Button variant="outlined" startIcon={<CheckCircleOutlineIcon />} onClick={() => handleVerify(row)}
                sx={{ flex: 1, textTransform: 'none', color: C.amberText, borderColor: C.amberBtnBorder }}>
                {t('tickets.markReviewed')}
              </Button>
            )}
            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => handleEdit(row)}
              sx={{ flex: 1, textTransform: 'none', color: C.blue, borderColor: C.border }}>
              {t('tickets.edit')}
            </Button>
            <Button variant="outlined" startIcon={<ImageSearchIcon />} disabled={!hasScan} onClick={() => setPreviewSrc(scanUrl)}
              aria-label={hasScan ? t('tickets.viewScan') : t('tickets.noScan')}
              sx={{ flex: 1, textTransform: 'none', color: C.blue, borderColor: C.border }}>
              {t('tickets.scan')}
            </Button>
            <IconButton aria-label={t('tickets.delete')} onClick={() => handleDelete(row)} sx={{ color: '#9AA6B2', border: `0.5px solid ${C.border}`, borderRadius: 1 }}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        )}
      </Box>
    );
  };

  const ghostBtnSx = {
    textTransform: 'none', fontSize: 13, borderColor: C.border, color: '#3A4654',
    '&:hover': { borderColor: C.blue, bgcolor: C.ghost },
  };

  const chip = (key, label) => {
    const active = statusFilter === key;
    return (
      <Box
        key={key}
        component="button"
        type="button"
        onClick={() => { setStatusFilter(key); setPage(0); }}
        sx={{
          height: { xs: 36, md: 28 }, px: 1.5, borderRadius: 20, cursor: 'pointer', fontSize: { xs: 14, md: 12.5 },
          display: 'inline-flex', alignItems: 'center', gap: 0.75,
          border: `0.5px solid ${active ? C.blue : C.border}`,
          bgcolor: active ? C.ghost : '#fff',
          color: active ? C.navy : '#48535F',
          fontWeight: active ? 600 : 400,
          '&:hover': { borderColor: C.blue },
        }}
      >
        {label}
        <Box component="span" sx={{ color: active ? C.blue : '#8A97A4', fontWeight: 600 }}>
          {key === 'ALL' ? counts.ALL : counts[key]}
        </Box>
      </Box>
    );
  };

  const typeChip = (key, label) => {
    const active = typeFilter === key;
    return (
      <Box
        key={key}
        component="button"
        type="button"
        onClick={() => { setTypeFilter(key); setStatusFilter('ALL'); setCustomerFilter('ALL'); setPage(0); }}
        sx={{
          height: { xs: 36, md: 28 }, px: 1.5, borderRadius: 20, cursor: 'pointer', fontSize: { xs: 14, md: 12.5 },
          display: 'inline-flex', alignItems: 'center', gap: 0.75,
          border: `0.5px solid ${active ? C.blue : C.border}`,
          bgcolor: active ? C.ghost : '#fff',
          color: active ? C.navy : '#48535F',
          fontWeight: active ? 600 : 400,
          '&:hover': { borderColor: C.blue },
        }}
      >
        {label}
        <Box component="span" sx={{ color: active ? C.blue : '#8A97A4', fontWeight: 600 }}>
          {typeCounts[key] ?? 0}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      {/* ---- Top bar ---- */}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#17202A' }}>{t('tickets.title')}</Typography>
          {subline && <Typography sx={{ fontSize: 12.5, color: '#6A7682' }}>{subline}</Typography>}
        </Box>
        {isPhone ? (
          <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
            {flaggedCount > 0 && (
              <Button
                variant="contained" disableElevation startIcon={<RuleIcon />}
                onClick={goVerifyFlagged}
                sx={{ flex: 1, textTransform: 'none', bgcolor: C.amberBtnBorder, color: '#fff', '&:hover': { bgcolor: C.amberText } }}
              >
                {t('tickets.verifyFlagged', { n: flaggedCount })}
              </Button>
            )}
            <Button
              variant="contained" disableElevation startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
              sx={{ flex: 1, textTransform: 'none', bgcolor: C.blue, color: C.ghost, '&:hover': { bgcolor: C.navy } }}
            >
              {t('tickets.add')}
            </Button>
            <IconButton aria-label={t('tickets.moreActions')} onClick={(e) => setActionsAnchor(e.currentTarget)} sx={{ border: `0.5px solid ${C.border}`, borderRadius: 1 }}>
              <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={() => setActionsAnchor(null)}>
              {isTicketDtls && (
                <MenuItem onClick={() => { setActionsAnchor(null); goReviewAll(); }}>{t('tickets.reviewAll', { n: sorted.length })}</MenuItem>
              )}
              <MenuItem onClick={() => { setActionsAnchor(null); if (selectMode) exitSelectMode(); else setSelectMode(true); }}>
                {selectMode ? t('tickets.exitSelect') : t('tickets.selectMultiple')}
              </MenuItem>
              <MenuItem disabled={!isTicketDtls} onClick={() => { setActionsAnchor(null); generateExcel(); }}>{t('tickets.export')}</MenuItem>
              <MenuItem onClick={() => { setActionsAnchor(null); setMergeOpen(true); }}>{t('tickets.mergeCustomers')}</MenuItem>
            </Menu>
          </Stack>
        ) : (
        <Stack direction="row" spacing={1}>
          {flaggedCount > 0 && (
            <Button
              variant="contained" size="small" disableElevation startIcon={<RuleIcon sx={{ fontSize: 18 }} />}
              onClick={goVerifyFlagged}
              sx={{ textTransform: 'none', fontSize: 13, bgcolor: C.amberBtnBorder, color: '#fff', '&:hover': { bgcolor: C.amberText } }}
            >
              {t('tickets.verifyFlagged', { n: flaggedCount })}
            </Button>
          )}
          {isTicketDtls && (
            <Button
              variant="outlined" size="small" startIcon={<RuleIcon sx={{ fontSize: 18 }} />}
              onClick={goReviewAll}
              sx={ghostBtnSx}
            >
              {t('tickets.reviewAll', { n: sorted.length })}
            </Button>
          )}
          <Button
            variant="outlined" size="small" startIcon={<ChecklistIcon sx={{ fontSize: 18 }} />}
            onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
            sx={{
              ...ghostBtnSx,
              ...(selectMode && { bgcolor: C.ghost, borderColor: C.blue, color: C.navy }),
            }}
          >
            {t('tickets.selectMultiple')}
          </Button>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon sx={{ fontSize: 18 }} />} onClick={generateExcel} disabled={!isTicketDtls} sx={ghostBtnSx}>
            {t('tickets.export')}
          </Button>
          <Button variant="outlined" size="small" startIcon={<CallMergeIcon sx={{ fontSize: 18 }} />} onClick={() => setMergeOpen(true)} sx={ghostBtnSx}>
            {t('tickets.mergeCustomers')}
          </Button>
          <Button
            variant="contained" size="small" disableElevation startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={() => setOpen(true)}
            sx={{ textTransform: 'none', fontSize: 13, bgcolor: C.blue, color: C.ghost, '&:hover': { bgcolor: C.navy } }}
          >
            {t('tickets.add')}
          </Button>
        </Stack>
        )}
      </Stack>

      {/* ---- shared company / date-range / plant / truck filters (drives data load) ---- */}
      <Box sx={{ mb: 2 }}>
        <Filters onFilterChange={handleFilterChange} showPlant initialFilters={filterData} />
      </Box>

      {/* Search progress bar — shown while tickets are being fetched. */}
      {loading && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography sx={{ fontSize: 12, color: '#6A7682', mt: 0.5 }}>{t('tickets.loading')}</Typography>
        </Box>
      )}

      {searched && !loading && tickets.length === 0 && <NoMatchingRecords sx={{ mb: 2 }} />}

      {isTicketDtls && (
        <Paper variant="outlined" sx={{ borderColor: C.border, overflow: 'hidden' }}>
          {/* ---- search + filter chips + sort ---- */}
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={1.5} sx={{ p: 1.5, borderBottom: `0.5px solid ${C.border}` }}>
            <TextField
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder={t('tickets.searchPlaceholder')}
              size="small"
              fullWidth={isPhone}
              sx={{ maxWidth: { md: 240 }, '& .MuiOutlinedInput-root': { height: { xs: 44, md: 32 }, fontSize: { xs: 16, md: 13 } } }}
              InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: '#7A8794' }} /></InputAdornment>) }}
            />
            {(typeCounts.HOUR > 0 || typeCounts.LOAD > 0) && (
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#6A7682' }}>{t('tickets.type')}</Typography>
                {typeChip('ALL', t('common.all'))}
                {typeChip('TON', t('tickets.tons'))}
                {typeCounts.HOUR > 0 && typeChip('HOUR', t('tickets.hours'))}
                {typeCounts.LOAD > 0 && typeChip('LOAD', t('tickets.loads'))}
              </Stack>
            )}
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#6A7682' }}>{t('tickets.filter')}</Typography>
              {chip('ALL', t('common.all'))}
              {counts.ERROR > 0 && chip('ERROR', t('status.needsFix'))}
              {chip('PRINTED', t('status.printed'))}
              {chip('PENDING', t('status.pending'))}
              {chip('VERIFIED', t('status.verified'))}
              {counts.INCOMPLETE > 0 && chip('INCOMPLETE', t('status.incomplete'))}
              {counts.NEEDS_RATE > 0 && chip('NEEDS_RATE', t('status.needsRate'))}
            </Stack>
            {(customerOptions.length > 0 || unassignedCustomerCount > 0) && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#6A7682' }}>{t('tickets.customer')}</Typography>
                <Select
                  value={customerFilter}
                  onChange={(e) => { setCustomerFilter(e.target.value); setPage(0); }}
                  size="small"
                  sx={{ height: 32, fontSize: 13, minWidth: 150, maxWidth: 220 }}
                >
                  <MenuItem value="ALL" sx={{ fontSize: 13 }}>{t('common.all')} ({typeFiltered.length})</MenuItem>
                  {customerOptions.map(([name, n]) => (
                    <MenuItem key={name} value={name} sx={{ fontSize: 13 }}>{name} ({n})</MenuItem>
                  ))}
                  {unassignedCustomerCount > 0 && (
                    <MenuItem value={UNASSIGNED} sx={{ fontSize: 13, fontStyle: 'italic' }}>
                      {t('tickets.unassigned')} ({unassignedCustomerCount})
                    </MenuItem>
                  )}
                </Select>
              </Stack>
            )}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: { md: 'auto' } }}>
              <Typography sx={{ fontSize: 11, fontWeight: 500, color: '#6A7682' }}>{t('tickets.sortBy')}</Typography>
              <Select value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(0); }} size="small" sx={{ height: 32, fontSize: 13, minWidth: 180 }}>
                {SORT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>{o.label}</MenuItem>)}
              </Select>
            </Stack>
          </Stack>

          {/* ---- loud remainder: unassigned tickets hidden by the Customer filter (§7) ---- */}
          {hiddenUnassignedCount > 0 && (
            <Box sx={{ px: 2, py: 0.75, borderBottom: `0.5px solid ${C.border}`, bgcolor: '#FAFBFC' }}>
              <Typography sx={{ fontSize: 12.5, color: '#6A7682' }}>
                {t('tickets.hiddenUnassigned', { n: hiddenUnassignedCount })}{' '}
                <Link
                  component="button" type="button"
                  onClick={() => { setCustomerFilter(UNASSIGNED); setPage(0); }}
                  sx={{ fontSize: 12.5, fontWeight: 600, verticalAlign: 'baseline' }}
                >
                  {t('tickets.view')}
                </Link>
              </Typography>
            </Box>
          )}

          {/* ---- select-mode banner ---- */}
          {selectMode && (
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1, bgcolor: C.amberBg, borderBottom: `0.5px solid ${C.amberBorder}`, color: C.amberText }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                <Typography sx={{ fontSize: 13 }}>{t('tickets.selectModeOn')}</Typography>
              </Stack>
              <Button variant="outlined" size="small" onClick={exitSelectMode} sx={{ textTransform: 'none', fontSize: 12.5, color: C.amberText, borderColor: C.amberBtnBorder, '&:hover': { borderColor: C.amberBtnBorder, bgcolor: 'rgba(186,117,23,0.08)' } }}>
                {t('tickets.exitSelect')}
              </Button>
            </Stack>
          )}

          {/* ---- bulk action bar ---- */}
          {selectMode && selected.size > 0 && (
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={1} sx={{ px: 2, py: 1, bgcolor: C.ghost, borderBottom: `0.5px solid ${C.pale}`, color: C.navy }}>
              <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                {t('tickets.selectedCount', { n: selected.size })}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {[
                  { label: t('tickets.bulkSetCustomer'), onClick: openBulkEdit },
                  { label: t('tickets.bulkInvoice'), onClick: bulkInvoice },
                  { label: t('tickets.bulkReviewed'), onClick: bulkMarkReviewed },
                  { label: t('tickets.bulkDelete'), onClick: bulkDelete },
                ].map(({ label, onClick }) => (
                  <Button
                    key={label} variant="outlined" size="small"
                    onClick={onClick}
                    sx={{ textTransform: 'none', fontSize: 12.5, color: C.blue, borderColor: C.blue, '&:hover': { borderColor: C.navy, bgcolor: 'rgba(24,95,165,0.06)' } }}
                  >
                    {label}
                  </Button>
                ))}
              </Stack>
            </Stack>
          )}

          {/* ---- phone: totals + one card per ticket ---- */}
          {isPhone && (
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ px: 2, py: 1.25, bgcolor: '#FAFBFC', borderBottom: `0.5px solid ${C.border}` }}>
              {[
                [t('tickets.count'), sorted.length],
                [t('tickets.tons'), phoneTotals.TON],
                ...(phoneTotals.HOUR ? [[t('tickets.hours'), phoneTotals.HOUR]] : []),
                ...(phoneTotals.LOAD ? [[t('tickets.loads'), phoneTotals.LOAD]] : []),
              ].map(([label, value]) => (
                <Box key={label}>
                  <Typography sx={{ fontSize: 11, color: '#6A7682', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</Typography>
                  <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#17202A', fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
                </Box>
              ))}
            </Stack>
          )}
          {isPhone ? (
            <Box>
              {pageRows.map(renderCard)}
              {searched && !loading && pageRows.length === 0 && <NoMatchingRecords sx={{ m: 2 }} />}
            </Box>
          ) : (
          <TableContainer>
            {/* Fixed layout + proportional column widths so the columns fill evenly (no slack pooling). */}
            <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 900 }}>
              <TableHead>
                <TableRow sx={{ '& th': { fontSize: 11.5, fontWeight: 600, color: '#6A7682', textTransform: 'uppercase', letterSpacing: '0.03em', borderBottom: `0.5px solid ${C.border}`, bgcolor: '#FAFBFC' } }}>
                  {selectMode && (
                    <TableCell padding="checkbox" sx={{ width: 36 }}>
                      <Checkbox size="small" checked={allSelected} indeterminate={someSelected} onChange={toggleAll} inputProps={{ 'aria-label': 'Select all' }} />
                    </TableCell>
                  )}
                  <TableCell sx={{ width: '9%', whiteSpace: 'nowrap', cursor: 'pointer' }} onClick={() => { setSortBy((s) => (s === 'DATE_DESC' ? 'DATE_ASC' : 'DATE_DESC')); setPage(0); }}>{t('col.date')}</TableCell>
                  <TableCell sx={{ width: '11%' }}>{t('col.ticketNo')}</TableCell>
                  <TableCell sx={{ width: '24%' }}>{t('col.customer')}</TableCell>
                  <TableCell align="right" sx={{ width: '7%', whiteSpace: 'nowrap' }}>{t('col.qty')}</TableCell>
                  <TableCell sx={{ width: '12%', whiteSpace: 'nowrap' }}>{isIvy ? 'Job' : 'FS'}</TableCell>
                  <TableCell sx={{ width: '8%', whiteSpace: 'nowrap' }}>{t('col.truck')}</TableCell>
                  <TableCell sx={{ width: '15%', whiteSpace: 'nowrap' }}>{t('col.lastUpdated')}</TableCell>
                  <TableCell align="right" sx={{ width: '15%', whiteSpace: 'nowrap' }}>{t('col.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((row) => {
                  const st = statusOf(row);
                  const isSel = selected.has(row.ticketId);
                  return (
                    <TableRow
                      key={row.ticketId}
                      hover
                      sx={{
                        '& td': { fontSize: 13, borderBottom: `0.5px solid #EEF2F6` },
                        ...(st.key === 'ERROR' && {
                          bgcolor: C.redBg,
                          boxShadow: `inset 3px 0 0 ${C.redText}`,
                          '&:hover': { bgcolor: '#FBDFDB' },
                        }),
                        ...(selectMode && isSel && { bgcolor: C.ghost }),
                      }}
                    >
                      {selectMode && (
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={isSel} onChange={() => toggleRow(row.ticketId)} inputProps={{ 'aria-label': `Select ticket ${row.ticketNo || ''}` }} />
                        </TableCell>
                      )}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.date)}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', color: C.blue }}>
                        {row.ticketNo}
                        {row.source === 'FIELD' && (
                          <Box component="span" sx={{ ml: 0.5, px: 0.5, py: 0.1, borderRadius: 1, bgcolor: C.amberBg, color: C.amberText, fontFamily: 'system-ui', fontSize: 10, fontWeight: 600 }}>
                            {row.rateType}{row.truckType && row.truckType !== 'ALL' ? ` · ${row.truckType}` : ''}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography noWrap title={row.customerName || ''} sx={{ fontSize: 13 }}>{row.customerName || '—'}</Typography>
                        {row.companyName && (
                          <Box component="span" sx={{ display: 'inline-block', mt: 0.25, px: 0.75, py: 0.1, borderRadius: 1, bgcolor: '#F0F3F7', color: '#6A7682', fontSize: 10.5 }}>
                            {row.companyName}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{qtyDisplay(row)}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {row.fsCode}
                        {needsRate(row) && (
                          <Tooltip title={t('tickets.noRateHelp')}>
                            <Box
                              component="button" type="button"
                              onClick={(e) => { e.stopPropagation(); openAddRate(row); }}
                              sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.75, px: 0.6, py: 0.1, borderRadius: 1, border: `0.5px solid ${C.amberBorder}`, bgcolor: C.amberBg, color: C.amberText, fontFamily: 'system-ui', fontSize: 10, fontWeight: 600, verticalAlign: 'middle', cursor: 'pointer', '&:hover': { bgcolor: '#F6E2BC' } }}
                            >
                              {t('tickets.noRateAdd')}
                            </Box>
                          </Tooltip>
                        )}
                        {needsRate(row) && row.suggestedFsCode && (
                          <Tooltip title={`This code isn't rated but "${row.suggestedFsCode}" is one character away and is — likely an OCR misread. Click to use it.`}>
                            <Box
                              component="button" type="button"
                              onClick={(e) => { e.stopPropagation(); applySuggestedCode(row); }}
                              sx={{ display: 'block', mt: 0.4, px: 0.6, py: 0.1, borderRadius: 1, border: `0.5px solid ${C.pale}`, bgcolor: C.ghost, color: C.navy, fontFamily: 'system-ui', fontSize: 10, fontWeight: 600, cursor: 'pointer', '&:hover': { bgcolor: C.pale }, textAlign: 'left' }}
                            >
                              {t('tickets.didYouMean', { code: row.suggestedFsCode })}
                            </Box>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: st.key === 'ERROR' ? C.redText : '#7A8794' }}>
                        {row.scanError ? (
                          <Tooltip
                            title={(
                              <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>
                                {row.scanError}. Edit this ticket to set a recognized truck — the error clears automatically.
                                <Box sx={{ mt: 0.75 }}>
                                  <Link
                                    component="button"
                                    type="button"
                                    onClick={() => openCreateTruck(row)}
                                    sx={{ color: '#fff', fontWeight: 600, textDecorationColor: 'rgba(255,255,255,0.6)', font: 'inherit', cursor: 'pointer' }}
                                  >
                                    {t('tickets.createTruck')}
                                  </Link>
                                </Box>
                              </Box>
                            )}
                          >
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: C.redText, fontWeight: 600 }}>
                              <WarningAmberIcon sx={{ fontSize: 15 }} />
                              {row.vehicle || t('tickets.noTruck')}
                            </Box>
                          </Tooltip>
                        ) : (
                          row.vehicle
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const who = row.updatedBy || row.createdBy;
                          const when = row.updatedAt || row.createdAt;
                          if (!who && !when) return <Box component="span" sx={{ color: '#B0BAC4' }}>—</Box>;
                          return (
                            <Stack spacing={0.1} sx={{ maxWidth: 160 }}>
                              {when && (
                                <Typography noWrap title={when} sx={{ fontSize: 12, color: '#48535F' }}>
                                  {when}
                                </Typography>
                              )}
                              <Typography noWrap title={who || ''} sx={{ fontSize: 11.5, color: '#8A97A4' }}>
                                {who || '—'}
                              </Typography>
                            </Stack>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          {st.key === 'PENDING' && (
                            <Tooltip title={row.source === 'FIELD' ? 'Review on Field Tickets' : t('tickets.markReviewed')}>
                              <IconButton size="small" aria-label="Mark reviewed" onClick={() => handleVerify(row)} sx={{ color: C.amberBtnBorder }}>
                                <CheckCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Button size="small" startIcon={<EditIcon sx={{ fontSize: 16 }} />} onClick={() => handleEdit(row)} sx={{ textTransform: 'none', fontSize: 12.5, color: C.blue, minWidth: 0 }}>
                            {t('tickets.edit')}
                          </Button>
                          {(() => {
                            const { hasScan, scanUrl } = scanOf(row);
                            return (
                              <Tooltip title={hasScan ? t('tickets.viewScan') : t('tickets.noScan')}>
                                <span>
                                  <IconButton size="small" aria-label="View scanned ticket" disabled={!hasScan} onClick={() => setPreviewSrc(scanUrl)}>
                                    <ImageSearchIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            );
                          })()}
                          <Tooltip title={t('tickets.delete')}>
                            <IconButton size="small" aria-label="Delete ticket" onClick={() => handleDelete(row)} sx={{ color: '#9AA6B2', '&:hover': { color: '#C0392B' } }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {searched && !loading && pageRows.length === 0 && (
                  <TableRow><TableCell colSpan={selectMode ? 9 : 8}><NoMatchingRecords sx={{ my: 1 }} /></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}

          {/* ---- footer / pagination ---- */}
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ px: 2, py: 1, borderTop: `0.5px solid ${C.border}` }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontSize: 12.5, color: '#6A7682' }}>{t('tickets.rowsPerPage')}</Typography>
              <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }} size="small" sx={{ height: 30, fontSize: 13 }}>
                {[10, 25, 50].map((n) => <MenuItem key={n} value={n} sx={{ fontSize: 13 }}>{n}</MenuItem>)}
              </Select>
              <Typography sx={{ fontSize: 12.5, color: '#6A7682' }}>
                {t('tickets.showing', { from: firstRow, to: lastRow, total: sorted.length })}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton size="small" aria-label="Previous page" disabled={safePage === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}><ChevronLeftIcon fontSize="small" /></IconButton>
              {isPhone ? (
                <Typography sx={{ fontSize: 14, color: '#48535F', px: 1 }}>{t('tickets.pageOf', { page: safePage + 1, pages: pageCount })}</Typography>
              ) : Array.from({ length: pageCount }, (_, i) => i).map((i) => (
                <Box
                  key={i} component="button" type="button" onClick={() => setPage(i)}
                  sx={{
                    minWidth: 28, height: 28, borderRadius: 1, cursor: 'pointer', fontSize: 13,
                    border: `0.5px solid ${i === safePage ? C.blue : C.border}`,
                    bgcolor: i === safePage ? C.blue : '#fff', color: i === safePage ? '#fff' : '#48535F',
                    '&:hover': { borderColor: C.blue },
                  }}
                >
                  {i + 1}
                </Box>
              ))}
              <IconButton size="small" aria-label="Next page" disabled={safePage >= pageCount - 1} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}><ChevronRightIcon fontSize="small" /></IconButton>
            </Stack>
          </Stack>
        </Paper>
      )}

      {/* ---- Add Ticket dialog ---- */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <AppDialog open={open} onClose={() => setOpen(false)}>
          <DialogTitle>{t('tickets.addTitle')}</DialogTitle>
          <DialogContent sx={{ mb: 1 }}>
            <Box mb={2} />
            <TextField
              select label={t('field.ticketType')} name="type" fullWidth value={newTicket.type}
              onChange={addHandleInputChange} sx={{ mb: 2 }}
              helperText="Weigh = tonnage/FS. Field = handwritten hourly or per-load (Hunter/Ivy)."
            >
              {TICKET_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
            <TextField label={t('field.date')} name="date" type="date" fullWidth value={newTicket.date} onChange={addHandleInputChange} InputLabelProps={{ shrink: true }} sx={{ mb: 2 }} />
            <TextField label={t('field.ticketNo')} name="ticketNo" fullWidth value={newTicket.ticketNo} onChange={addHandleInputChange} sx={{ mb: 2 }} />
            <TextField label={t('field.truckNo')} name="vehicle" fullWidth value={newTicket.vehicle} onChange={addHandleInputChange} sx={{ mb: 2 }} />

            {newTicket.type === 'TON' ? (
              <>
                <TextField
                  select label={t('field.plant')} name="companyName" fullWidth
                  value={newTicket.companyName} onChange={addHandleInputChange} sx={{ mb: 2 }}
                >
                  {visiblePlants(role, plants).map((p) => (
                    <MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>
                  ))}
                </TextField>
                <TextField label={t('field.customer')} name="customerName" fullWidth value={newTicket.customerName} onChange={addHandleInputChange} sx={{ mb: 2 }} />
                <TextField label={t('field.tons')} name="ton" fullWidth inputProps={{ inputMode: 'decimal' }} value={newTicket.ton} onChange={addHandleInputChange} sx={{ mb: 2 }} />
                <TextField label={codeLbl} name="fsCode" fullWidth value={newTicket.fsCode} onChange={addHandleInputChange} sx={{ mb: 2 }} />
                {!isIvy && (
                  <TextField label={t('field.jobCode')} name="jobCode" fullWidth value={newTicket.jobCode} onChange={addHandleInputChange} sx={{ mb: 2 }} />
                )}
              </>
            ) : (
              <>
                <TextField
                  select label={t('field.truckType')} name="truckType" fullWidth
                  value={newTicket.truckType} onChange={addHandleInputChange} sx={{ mb: 2 }}
                  helperText="Drives the rate (Belly, End Dump, …)"
                >
                  {FIELD_TRUCK_TYPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </TextField>
                <TextField label={t('field.billedTo')} name="customerName" fullWidth value={newTicket.customerName} onChange={addHandleInputChange} sx={{ mb: 2 }} />
                <TextField
                  label={newTicket.type === 'HOUR' ? t('field.hours') : t('field.loads')} name="quantity" type="number" fullWidth
                  value={newTicket.quantity} onChange={addHandleInputChange} sx={{ mb: 2 }}
                  InputProps={{ inputProps: { min: 0, step: newTicket.type === 'HOUR' ? 0.25 : 1 } }}
                />
                <TextField label={t('field.origin')} name="producerLocation" fullWidth value={newTicket.producerLocation} onChange={addHandleInputChange} sx={{ mb: 2 }} />
                <TextField label={t('field.destination')} name="destination" fullWidth value={newTicket.destination} onChange={addHandleInputChange} sx={{ mb: 2 }} />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)} color="secondary">{t('common.cancel')}</Button>
            <Button onClick={handleAddTicket} variant="contained" disableElevation>{t('common.add')}</Button>
          </DialogActions>
        </AppDialog>
      </LocalizationProvider>

      {/* ---- Edit Ticket dialog ---- */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <AppDialog open={editId != null} onClose={handleCancel} fullWidth maxWidth="md">
          <DialogTitle>Edit ticket{formValues.ticketNo ? ` ${formValues.ticketNo}` : ''}</DialogTitle>
          <DialogContent sx={{ mb: 1 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 1 }}>
              {/* scanned ticket beside the fields so misreads (FS letter, customer, date) are easy to catch */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {formValues.documentId ? (
                  <>
                    {/* Drivers photograph tickets sideways on a seat, and the scan is read here AND
                        attached to the settlement the dispatcher receives. The model records the
                        orientation at scan time; this is the correction when it got it wrong, and the
                        only way to set it for scans archived before that existed. Absolute degrees,
                        never a delta, so the button always sends what it is showing. */}
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                      <Tooltip title="Rotate left">
                        <span>
                          <IconButton size="small" disabled={rotating}
                            onClick={() => rotateScan(-90)}>
                            <RotateLeftIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Rotate right">
                        <span>
                          <IconButton size="small" disabled={rotating}
                            onClick={() => rotateScan(90)}>
                            <RotateRightIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Typography variant="caption" sx={{ color: '#6A7682' }}>
                        Rotate the scan — saved for the settlement PDF too
                      </Typography>
                    </Stack>
                    <Tooltip title="Click to view full size">
                      <Box
                        onClick={() => setPreviewSrc(`${apiUrl}/api/documents/${formValues.documentId}?v=${scanVersion}`)}
                        sx={{ maxHeight: 540, overflow: 'auto', border: `1px solid ${C.border}`, borderRadius: 1, bgcolor: '#1A2129', cursor: 'zoom-in' }}
                      >
                        <img src={`${apiUrl}/api/documents/${formValues.documentId}?v=${scanVersion}`} alt="Scanned ticket" style={{ width: '100%', display: 'block' }} />
                      </Box>
                    </Tooltip>
                  </>
                ) : (
                  <Box sx={{ p: 2, border: `1px dashed ${C.border}`, borderRadius: 1, color: '#6A7682', fontSize: 13 }}>
                    No scan image stored for this ticket.
                  </Box>
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
            <TextField label="Date" name="date" fullWidth value={formValues.date ?? ''} onChange={handleInputChange} sx={{ mb: 2 }} />
            <TextField label="Ticket No" name="ticketNo" fullWidth value={formValues.ticketNo ?? ''} onChange={handleInputChange} sx={{ mb: 2 }} />
            <TextField
              select
              label="Plant"
              name="companyName"
              fullWidth
              value={formValues.companyName ?? ''}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
            >
              {visiblePlants(role, plants).map((p) => (
                <MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>
              ))}
            </TextField>
            <TextField label="Customer" name="customerName" fullWidth value={formValues.customerName ?? ''} onChange={handleInputChange} sx={{ mb: 2 }} />
            <TextField label="Ton" name="ton" fullWidth value={formValues.ton ?? ''} onChange={handleInputChange} sx={{ mb: 2 }} />
            <TextField label={codeLbl} name="fsCode" fullWidth value={formValues.fsCode ?? ''} onChange={handleInputChange} sx={{ mb: needsRate(formValues) ? 0.5 : 2 }} />
            {needsRate(formValues) && (
              <Box sx={{ mb: 2 }}>
                <Link
                  component="button" type="button"
                  onClick={() => openAddRate(formValues)}
                  sx={{ fontSize: 12.5, fontWeight: 600, color: C.amberBtnBorder }}
                >
                  No rate for this code — add a rate
                </Link>
              </Box>
            )}
            {!isIvy && (
              <TextField label="Job Code" name="jobCoe" fullWidth value={formValues.jobCoe ?? ''} onChange={handleInputChange} sx={{ mb: 2 }} />
            )}
            <TextField label="Truck No" name="vehicle" fullWidth value={formValues.vehicle ?? ''} onChange={handleInputChange} sx={{ mb: 2 }} />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel} color="secondary">Cancel</Button>
            <Button onClick={handleSave} variant="contained" disableElevation>Save</Button>
          </DialogActions>
        </AppDialog>
      </LocalizationProvider>

      {/* ---- inline create-truck dialog (fix an unrecognized-truck row without leaving Tickets) ---- */}
      <AppDialog open={truckDialogRow != null} onClose={closeCreateTruck} fullWidth maxWidth="xs">
        <DialogTitle>Create truck</DialogTitle>
        <DialogContent sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 13, color: '#6A7682', mb: 2 }}>
            Adds this truck to the master and re-links the flagged ticket
            {truckDialogRow?.ticketNo ? ` ${truckDialogRow.ticketNo}` : ''} so its error clears —
            along with every other ticket on the same truck.
            Only do this for a genuinely new truck — if the number was just misread, use Edit instead.
          </Typography>
          {/* The ticket names the truck's owner. Show it: a truck number is unique only within a
              company, so the operator is the one deciding which company this truck belongs to. */}
          {truckDialogRow?.truckCompany && (
            <Typography sx={{ fontSize: 12.5, mb: 2, px: 1.25, py: 0.75,
              bgcolor: '#E6F1FB', color: '#0C447C', borderRadius: 1 }}>
              Ticket says this truck belongs to <b>{truckDialogRow.truckCompany}</b>
              {matchCompanyId(truckDialogRow.truckCompany)
                ? ' — company preselected below.'
                : ' — no matching company yet; add it below or pick one.'}
            </Typography>
          )}
          <TextField
            label="Truck Number" name="number" fullWidth required
            value={truckForm.number}
            onChange={(e) => setTruckForm((f) => ({ ...f, number: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            select label="Company" name="companyId" fullWidth required
            value={truckForm.companyId}
            onChange={(e) => setTruckForm((f) => ({ ...f, companyId: e.target.value }))}
            sx={{ mb: addingCompany ? 1 : 0.5 }}
          >
            {companies.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
          {addingCompany ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
              <TextField
                label="New company name" size="small" fullWidth autoFocus
                value={newCompanyName}
                onChange={(e) => setNewCompanyName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddCompanyInline(); } }}
              />
              <Button onClick={handleAddCompanyInline} disabled={savingCompany} variant="contained" disableElevation>
                Add
              </Button>
              <Button onClick={() => { setAddingCompany(false); setNewCompanyName(''); }} color="secondary">
                Cancel
              </Button>
            </Box>
          ) : (
            <Button
              size="small" startIcon={<AddIcon />} onClick={() => setAddingCompany(true)}
              sx={{ textTransform: 'none', pl: 0, mb: 2, alignSelf: 'flex-start' }}
            >
              Add company
            </Button>
          )}
          <TextField
            select label="Truck Type (optional)" name="truckType" fullWidth
            value={truckForm.truckType}
            onChange={(e) => setTruckForm((f) => ({ ...f, truckType: e.target.value }))}
          >
            <MenuItem value="">—</MenuItem>
            <MenuItem value="BELLY">Belly Dump</MenuItem>
            <MenuItem value="END">End Dump</MenuItem>
            <MenuItem value="FLAT">Flatbed</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateTruck} color="secondary">Cancel</Button>
          <Button onClick={handleCreateTruck} variant="contained" disableElevation>Create truck</Button>
        </DialogActions>
      </AppDialog>

      {/* ---- scanned ticket preview ---- */}
      <AppDialog open={previewSrc != null} onClose={() => setPreviewSrc(null)} fullWidth maxWidth="md">
        <DialogTitle>Scanned ticket</DialogTitle>
        <DialogContent>
          {previewSrc != null && <img src={previewSrc} alt="Scanned ticket" style={{ width: '100%', height: 'auto' }} />}
        </DialogContent>
      </AppDialog>

      {/* ---- inline Add-rate popup (fix a "No rate" ticket without leaving) ---- */}
      <AppDialog open={!!rateDialog} onClose={() => setRateDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>{rateDialog?.existing ? 'Update rate' : 'Add rate'}</DialogTitle>
        <DialogContent sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 13, color: '#6A7682', mb: 2 }}>
            Sets the rate for this {codeLbl} so the ticket prices correctly. The rate applies to tickets dated
            <strong> on or after the effective date</strong> — it defaults to your search start date so it
            covers the whole period. Saved to the Rate Cards.
          </Typography>
          {rateDialog?.existing && (
            <Box sx={{ mb: 2, p: 1, borderRadius: 1, bgcolor: C.ghost, border: `0.5px solid ${C.pale}` }}>
              <Typography sx={{ fontSize: 12.5, color: C.navy }}>
                Existing rate: <b>${Number(rateDialog.existing.rate || 0).toFixed(2)}</b> effective {formatDate(rateDialog.existing.effectiveDate)}.
                <br />“Update rate” changes it; “Add new date” keeps it and adds another effective-dated rate.
              </Typography>
            </Box>
          )}
          {rateDialog && (
            <>
              <TextField label={codeLbl} fullWidth value={rateDialog.fsCode}
                onChange={(e) => setRateDialog((p) => ({ ...p, fsCode: e.target.value }))} sx={{ mb: 2 }} />
              <TextField label="Rate ($)" type="number" fullWidth autoFocus value={rateDialog.rate}
                onChange={(e) => setRateDialog((p) => ({ ...p, rate: e.target.value }))} sx={{ mb: 2 }}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }} />
              <TextField label="Effective date" type="date" fullWidth value={rateDialog.effectiveDate}
                onChange={(e) => setRateDialog((p) => ({ ...p, effectiveDate: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRateDialog(null)} color="secondary">Cancel</Button>
          {rateDialog?.existing ? (
            <>
              <Button onClick={() => submitRate('create')} disabled={savingRate} sx={{ textTransform: 'none' }}>
                Add new date
              </Button>
              <Button onClick={() => submitRate('update')} variant="contained" disableElevation disabled={savingRate}>
                Update rate
              </Button>
            </>
          ) : (
            <Button onClick={() => submitRate('create')} variant="contained" disableElevation disabled={savingRate}>
              Save rate
            </Button>
          )}
        </DialogActions>
      </AppDialog>

      {/* ---- field-ticket review popup (scan image beside editable fields + verify) ---- */}
      <AppDialog open={!!fieldEditing} onClose={() => setFieldEditing(null)} fullWidth maxWidth="lg"
        PaperProps={{ sx: { height: { xs: 'auto', md: '90vh' } } }}>
        <DialogTitle sx={{ pr: 6 }}>
          Review field ticket {fieldEditing?.ticketNo ? `#${fieldEditing.ticketNo}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {fieldEditing && (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} sx={{ mt: 1, height: '100%' }}>
              <Box sx={{ flex: 1.25, minWidth: 0, display: 'flex' }}>
                {fieldEditing.hasImage ? (
                  <a href={`${apiUrl}/api/field-tickets/${fieldEditing.id}/image`} target="_blank" rel="noreferrer"
                    style={{ display: 'block', width: '100%' }} title="Open full-size in a new tab">
                    <img
                      src={`${apiUrl}/api/field-tickets/${fieldEditing.id}/image`}
                      alt="scan"
                      style={{ width: '100%', height: 'auto', maxHeight: '80vh', objectFit: 'contain',
                        border: '1px solid #ddd', borderRadius: 4, cursor: 'zoom-in' }}
                    />
                  </a>
                ) : (
                  <Typography variant="body2" sx={{ color: '#6A7682' }}>No scan image stored.</Typography>
                )}
              </Box>
              <Stack spacing={2} sx={{ flex: 1 }}>
                <Stack direction="row" spacing={2}>
                  <TextField label="Ticket #" value={fieldEditing.ticketNo ?? ''} onChange={(e) => setF('ticketNo', e.target.value)} fullWidth size="small" />
                  <TextField label="Truck #" value={fieldEditing.truckNo ?? ''} onChange={(e) => setF('truckNo', e.target.value)} fullWidth size="small" />
                </Stack>
                <TextField label="Date" type="date" value={fieldEditing.ticketDate ?? ''} onChange={(e) => setF('ticketDate', e.target.value)} InputLabelProps={{ shrink: true }} fullWidth size="small" />
                <TextField label="Billed To (customer)" value={fieldEditing.billedTo ?? ''} onChange={(e) => setF('billedTo', e.target.value)} fullWidth size="small" />
                <Stack direction="row" spacing={2}>
                  <TextField label="Truck type" select value={fieldEditing.truckType ?? ''} onChange={(e) => setF('truckType', e.target.value)} fullWidth size="small">
                    {FIELD_TRUCK_TYPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                  <TextField label="Basis" select value={fieldEditing.basis ?? 'LOAD'} onChange={(e) => setF('basis', e.target.value)} fullWidth size="small">
                    <MenuItem value="HOUR">Hourly</MenuItem>
                    <MenuItem value="LOAD">Per load</MenuItem>
                  </TextField>
                  <TextField label={fieldEditing.basis === 'HOUR' ? 'Hours' : 'Loads'} type="number" value={fieldEditing.quantity ?? ''} onChange={(e) => setF('quantity', e.target.value)} fullWidth size="small" />
                  {fieldEditing.basis === 'HOUR' && (
                    <TextField label="Loads" type="number" value={fieldEditing.loads ?? ''} onChange={(e) => setF('loads', e.target.value)} fullWidth size="small" />
                  )}
                </Stack>
                <Stack direction="row" spacing={2}>
                  <TextField label="From (producer)" value={fieldEditing.producerLocation ?? ''} onChange={(e) => setF('producerLocation', e.target.value)} fullWidth size="small" />
                  <TextField label="To (destination)" value={fieldEditing.destination ?? ''} onChange={(e) => setF('destination', e.target.value)} fullWidth size="small" />
                </Stack>
                <Stack direction="row" spacing={2}>
                  <TextField label="Driver" value={fieldEditing.driver ?? ''} onChange={(e) => setF('driver', e.target.value)} fullWidth size="small" />
                  <TextField label="Foreman" value={fieldEditing.foreman ?? ''} onChange={(e) => setF('foreman', e.target.value)} fullWidth size="small" />
                </Stack>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFieldEditing(null)} color="secondary">Cancel</Button>
          <Button
            variant="outlined" color="success"
            onClick={async () => { const ok = await saveFieldEdit(); if (ok) { setFieldEditing(null); await refetch(); } }}
          >
            Save
          </Button>
          {fieldEditing?.reviewStatus !== 'VERIFIED' && (
            <Button
              variant="contained" color="success" disableElevation
              onClick={async () => { const ok = await saveFieldEdit(); if (!ok) return; const v = await verifyField(); if (v) { setFieldEditing(null); await refetch(); } }}
            >
              Save &amp; Verify
            </Button>
          )}
        </DialogActions>
      </AppDialog>

      {/* ---- bulk edit: set Customer / load type on the selected tickets ---- */}
      <AppDialog open={bulkEditOpen} onClose={() => !bulkBusy && setBulkEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Update {selected.size} selected ticket{selected.size === 1 ? '' : 's'}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: '#6A7682', mb: 2 }}>
            Set any of the fields below and apply to every selected ticket at once. Blank fields are left
            unchanged. Truck type and Basis apply to field tickets only.
          </Typography>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Autocomplete
              freeSolo options={bulkCustomerOptions} value={bulkCustomer}
              onChange={(e, v) => setBulkCustomer(v || '')} onInputChange={(e, v) => setBulkCustomer(v || '')}
              renderInput={(p) => <TextField {...p} size="small" label="Set Customer / Billed To" placeholder="(keep unchanged)" />}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select size="small" fullWidth label="Set Truck type" value={bulkTruckType}
                onChange={(e) => setBulkTruckType(e.target.value)}>
                <MenuItem value="">(keep unchanged)</MenuItem>
                {FIELD_TRUCK_TYPES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
              <TextField select size="small" fullWidth label="Set Basis" value={bulkBasis}
                onChange={(e) => setBulkBasis(e.target.value)}>
                <MenuItem value="">(keep unchanged)</MenuItem>
                <MenuItem value="HOUR">Hourly</MenuItem>
                <MenuItem value="LOAD">Per load</MenuItem>
              </TextField>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkEditOpen(false)} color="secondary" disabled={bulkBusy}>Cancel</Button>
          <Button variant="contained" disableElevation disabled={bulkBusy} onClick={applyBulkEdit}>
            {bulkBusy ? 'Applying…' : 'Apply to selected'}
          </Button>
        </DialogActions>
      </AppDialog>

      {/* ---- merge customer-name spelling variants (shared dialog) ---- */}
      <CustomerMergeDialog
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        email={user?.email}
        onChanged={() => refetch()}
      />
    </Box>
  );
};

export default Ticket;
